import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { GET_LIST, useClient } from "@peracto/client";
import {
  HydraTable,
  Heading,
  DateRangeFilter,
  SelectFilter,
} from "@peracto/peracto-ui";
import { faDownload } from "@fortawesome/pro-regular-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast } from "react-toastify";
import axios from "axios";
import download from "downloadjs";
import dayjs from "dayjs";

const DownloadCell = ({ row }) => {
  const [downloadingOriginal, setDownloadingOriginal] = useState(false);
  const [downloadingImported, setDownloadingImported] = useState(false);
  const [downloadingFailed, setDownloadingFailed] = useState(false);
  const [downloadingConfiguration, setDownloadingConfiguration] = useState(false);

  const apiBase = process.env.REACT_APP_API;
  const numericId = row.original.originId;
  const status = row.original.status;
  const importedCsvPath = row.original.importedCsvPath;
  const failedCsvPath = row.original.failedCsvPath;

  if (status === "pending" || status === "processing") {
    return <span className="text-muted text-sm">—</span>;
  }

  const handleDownload = async (type) => {
    if (type === "configuration") {
      setDownloadingConfiguration(true);
      try {
        const { data } = await axios.get(
          `${apiBase}/babbage-account-configuration-import/${numericId}/download/configuration`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        download(JSON.stringify(data, null, 2), "configuration.json", "application/json");
      } catch (e) {
        console.error(e);
        toast.error("There was an error downloading the configuration.");
      } finally {
        setDownloadingConfiguration(false);
      }
      return;
    }

    const urlMap = {
      imported: `${apiBase}/babbage-account-configuration-import/${numericId}/download/imported`,
      failed: `${apiBase}/babbage-account-configuration-import/${numericId}/download/failed`,
      original: `${apiBase}/babbage-account-configuration-import/${numericId}/download`,
    };
    const setDownloadingMap = {
      imported: setDownloadingImported,
      failed: setDownloadingFailed,
      original: setDownloadingOriginal,
    };

    const setDownloading = setDownloadingMap[type] || setDownloadingOriginal;
    const url = urlMap[type];

    setDownloading(true);
    try {
      const { data } = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const presignedUrl = data.preSignedUrl;
      if (!presignedUrl || !presignedUrl.startsWith("https://")) {
        toast.error("There was an error generating the download link.");
        return;
      }
      window.location.href = presignedUrl;
    } catch (e) {
      console.error(e);
      toast.error("There was an error generating the download link.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="d-flex" style={{ gap: "0.5rem" }}>
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => handleDownload("original")}
        disabled={downloadingOriginal}
        data-testid="download-original"
      >
        <FontAwesomeIcon icon={faDownload} className="mr-1" />
        {downloadingOriginal ? "..." : "Original"}
      </button>
      {importedCsvPath && (
        <button
          className="btn btn-sm btn-outline-success"
          onClick={() => handleDownload("imported")}
          disabled={downloadingImported}
          data-testid="download-imported"
        >
          <FontAwesomeIcon icon={faDownload} className="mr-1" />
          {downloadingImported ? "..." : "Imported"}
        </button>
      )}
      {failedCsvPath && (
        <button
          className="btn btn-sm btn-outline-danger"
          onClick={() => handleDownload("failed")}
          disabled={downloadingFailed}
          data-testid="download-failed"
        >
          <FontAwesomeIcon icon={faDownload} className="mr-1" />
          {downloadingFailed ? "..." : "Failed"}
        </button>
      )}
      <button
        className="btn btn-sm btn-outline-info"
        onClick={() => handleDownload("configuration")}
        disabled={downloadingConfiguration}
        data-testid="download-configuration"
      >
        <FontAwesomeIcon icon={faDownload} className="mr-1" />
        {downloadingConfiguration ? "..." : "Configuration"}
      </button>
    </div>
  );
};

const STATUS_OPTIONS = [
  { value: "", label: "Please select..." },
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "complete", label: "Complete" },
  { value: "failed", label: "Failed" },
];

const STATUS_BADGE = {
  pending: "badge badge-warning",
  processing: "badge badge-primary",
  complete: "badge badge-success",
  failed: "badge badge-danger",
};

export const ImportLogsList = () => {
  const [selectedColumns, onSelectColumn] = useState({});
  const { client } = useClient();

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "originId",
        sortType: "basic",
        disableFilters: true,
        selectable: false,
        Cell: ({ cell: { value } }) => `Import Log ${value}`,
      },
      {
        Header: "Status",
        accessor: "status",
        sortType: "basic",
        Filter: (props) => (
          <SelectFilter label="Status" options={STATUS_OPTIONS} {...props} />
        ),
        Cell: ({ cell: { value } }) => (
          <span className={STATUS_BADGE[value] || "badge badge-secondary"}>
            {value}
          </span>
        ),
        selectable: false,
      },
      {
        Header: "Imported Count",
        accessor: "importedCount",
        disableFilters: true,
        disableSorting: true,
        selectable: false,
        Cell: ({ cell: { value } }) => (value != null ? value : "—"),
      },
      {
        Header: "Failed Count",
        accessor: "failedCount",
        disableFilters: true,
        disableSorting: true,
        selectable: false,
        Cell: ({ cell: { value } }) => (value != null ? value : "—"),
      },
      {
        Header: "Processed Count",
        accessor: "processedCount",
        disableFilters: true,
        disableSorting: true,
        selectable: false,
        Cell: ({ cell: { value } }) => (value != null ? value : "—"),
      },
      {
        Header: "Non-Configurable Count",
        accessor: "nonConfigurableCount",
        disableFilters: true,
        disableSorting: true,
        selectable: false,
        Cell: ({ cell: { value } }) => (value != null ? value : "—"),
      },
      {
        Header: "Created At",
        accessor: "createdAt",
        Filter: (props) => <DateRangeFilter label="Created At" {...props} />,
        Cell: ({ cell: { value } }) =>
          value ? dayjs(value).format("DD MMM YYYY HH:mm:ss") : "—",
        selectable: false,
      },
      {
        Header: "Modified At",
        accessor: "modifiedAt",
        disableFilters: true,
        disableSorting: true,
        selectable: false,
        Cell: ({ cell: { value } }) =>
          value ? dayjs(value).format("DD MMM YYYY HH:mm:ss") : "—",
      },
      {
        Header: "Downloads",
        selectable: false,
        disableSorting: true,
        disableFilters: true,
        Cell: ({ row }) => <DownloadCell row={row} />,
      },
    ],
    [],
  );

  return (
    <>
      <Helmet>
        <title>Babbage Import Logs | Peracto</title>
      </Helmet>
      <Heading name="Babbage Import Logs" />

      <div className="row">
        <div className="col">
          <div className="card card--transparent">
            <div className="card-body">
              <HydraTable
                columns={columns}
                selectedColumns={selectedColumns}
                onFetchData={(options) =>
                  client(
                    GET_LIST,
                    "babbage-account-configuration-import",
                    options,
                  )
                }
                onSelectColumn={onSelectColumn}
                defaultFilters={{
                  status: "",
                  createdAt: "",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
